<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/interceptor.services';

const { t, locale } = useI18n({ useScope: 'global' });

// ── Types ────────────────────────────────────────────────────────────────────
type DocType =
  'LEGAL_NOTICE' | 'SALES_TERMS' | 'COOKIE_POLICY' | 'PLATFORM_TERMS';
type DocLevel = 'PLATFORM' | 'ORGANIZATION' | 'STORE';

interface LegalVersion {
  version: number;
  versionId: string;
  effectiveFrom: string | null;
  title: string;
  content: string;
  translationFallback: boolean;
}

interface LegalDoc {
  id: string;
  type: DocType;
  level: DocLevel;
  organizationId: string | null;
  restaurantId: string | null;
}

interface DocEntry {
  id: string;
  type: DocType;
  level: DocLevel;
  organizationId: string | null;
  restaurantId: string | null;
  organizationName: string;
  restaurantName: string;
  draftVersionId: string | null;
  publishedVersion: number | null;
  editorContent: string;
  editorTitle: string;
  saving: boolean;
  saved: boolean;
  error: string | null;
  // Vrai quand l'erreur vient d'une version devenue PUBLISHED entre-temps :
  // affiche un bouton pour créer directement une nouvelle version brouillon.
  needsNewVersion: boolean;
  // Date/heure d'entrée en vigueur programmée (vide = publication immédiate).
  effectiveFrom: string;
}

const DOC_TYPES: DocType[] = [
  'LEGAL_NOTICE',
  'SALES_TERMS',
  'COOKIE_POLICY',
  'PLATFORM_TERMS',
];
const DOC_LEVELS: DocLevel[] = ['PLATFORM', 'ORGANIZATION', 'STORE'];

// ── List state ───────────────────────────────────────────────────────────────
const docs = ref<DocEntry[]>([]);
const isLoading = ref(false);
const loadError = ref<string | null>(null);
const typeFilter = ref<DocType | ''>('');
const levelFilter = ref<DocLevel | ''>('');

const filteredDocs = computed(() =>
  docs.value.filter(
    (d) =>
      (!typeFilter.value || d.type === typeFilter.value) &&
      (!levelFilter.value || d.level === levelFilter.value),
  ),
);

function makeEntry(doc: LegalDoc): DocEntry {
  return {
    id: doc.id,
    type: doc.type,
    level: doc.level,
    organizationId: doc.organizationId,
    restaurantId: doc.restaurantId,
    organizationName: '',
    restaurantName: '',
    draftVersionId: null,
    publishedVersion: null,
    editorContent: '',
    editorTitle: '',
    saving: false,
    saved: false,
    error: null,
    needsNewVersion: false,
    effectiveFrom: '',
  };
}

// ── Load all legal documents (all levels/types) ───────────────────────────────
async function loadAll() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const res = await axiosInstance.get('admin/legal-documents', {
      headers: { 'x-client-app': 'admin-back-office' },
    });
    const list: LegalDoc[] = res.data?.data ?? res.data ?? [];
    docs.value = list.map(makeEntry);
    await Promise.all(
      docs.value.map(async (entry) => {
        await loadVersions(entry);
        await resolveEntityNames(entry);
      }),
    );
  } catch {
    loadError.value = t('terms.loadError');
  } finally {
    isLoading.value = false;
  }
}

async function resolveEntityNames(entry: DocEntry) {
  try {
    if (entry.level === 'ORGANIZATION' && entry.organizationId) {
      const res = await axiosInstance.get(
        `organizations/get/${entry.organizationId}`,
      );
      const data = res.data?.data ?? res.data;
      entry.organizationName = data?.name ?? data?.designation ?? '';
    } else if (entry.level === 'STORE' && entry.restaurantId) {
      const res = await axiosInstance.get(
        `restaurants/get/${entry.restaurantId}`,
      );
      const data = res.data?.data ?? res.data;
      entry.restaurantName = data?.name ?? data?.designation ?? '';
    }
  } catch {
    // affichage sans nom résolu si l'entité n'est plus accessible
  }
}

async function loadVersions(entry: DocEntry) {
  try {
    const res = await axiosInstance.get(
      `admin/legal-documents/${entry.id}/versions`,
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    const versions: LegalVersion[] = res.data?.data ?? res.data ?? [];
    const top = versions[0];
    if (top) {
      // La version la plus récente est un brouillon modifiable si et
      // seulement si elle n'est pas encore entrée en vigueur : effectiveFrom
      // === null signifie qu'elle n'a jamais été publiée ni programmée. Une
      // version publiée ou archivée a toujours un effectiveFrom renseigné et
      // est immuable côté API.
      const isDraft = top.effectiveFrom === null;
      entry.draftVersionId = isDraft ? top.versionId : null;
      entry.editorContent = top.content ?? '';
      entry.editorTitle = top.title ?? '';
      const published = versions.find((v) => v.effectiveFrom !== null);
      entry.publishedVersion = published?.version ?? null;
    }
  } catch {
    // ignore
  }
}

// ── Create draft version : POST toujours envoyé, sans court-circuit ──────────
// Appelée uniquement quand on sait qu'il n'y a pas de draftVersionId connu
// (bouton "Créer le brouillon") — l'utilisateur veut explicitement déclencher
// cet appel, pas se faire servir un id en cache sans requête réseau.
async function createDraftVersion(entry: DocEntry): Promise<string> {
  try {
    const res = await axiosInstance.post(
      `admin/legal-documents/${entry.id}/versions`,
      { translations: buildTranslations(entry) },
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    const created = res.data?.data ?? res.data;
    const vid = created?.versionId as string | undefined;
    if (vid) {
      entry.draftVersionId = vid;
      if (created?.content != null) entry.editorContent = created.content;
      if (created?.title != null) entry.editorTitle = created.title;
      return vid;
    }
    // Réponse inattendue : on refetch pour rester sûr.
    await loadVersions(entry);
    if (entry.draftVersionId) return entry.draftVersionId;
    throw new Error('draft not found after creation');
  } catch (e: any) {
    if (e?.response?.status === 422) {
      // Un brouillon existe déjà côté serveur (course avec un autre onglet
      // par ex.) : on récupère son id plutôt que d'échouer.
      const existingId =
        e?.response?.data?.data?.versionId ??
        e?.response?.data?.data?.id ??
        e?.response?.data?.id;
      if (existingId) {
        entry.draftVersionId = existingId;
        return existingId;
      }
      await loadVersions(entry);
      if (entry.draftVersionId) return entry.draftVersionId;
    }
    throw e;
  }
}

// ── Get or create a draft version (always reads API first) ───────────────────
async function getOrCreateDraft(entry: DocEntry): Promise<string> {
  await loadVersions(entry);
  if (entry.draftVersionId) return entry.draftVersionId;
  return createDraftVersion(entry);
}

function buildTranslations(entry: {
  editorTitle: string;
  editorContent: string;
}) {
  return [
    {
      languageCode: 'fr',
      title: entry.editorTitle,
      content: entry.editorContent,
    },
    {
      languageCode: 'en',
      title: entry.editorTitle,
      content: entry.editorContent,
    },
    {
      languageCode: 'nl',
      title: entry.editorTitle,
      content: entry.editorContent,
    },
  ];
}

// ── Save draft (update the current draft version) ─────────────────────────────
async function saveDraft(entry: DocEntry) {
  entry.saving = true;
  entry.saved = false;
  entry.error = null;
  try {
    // Pas de brouillon connu : on appelle toujours Create draft version
    // (translations omis → reprend le contenu en vigueur), sans jamais se
    // faire servir un id en cache sans requête réseau — l'utilisateur a
    // cliqué explicitement sur "Créer le brouillon". S'il y a déjà un
    // brouillon connu, on va directement à l'update (PUT).
    const hadDraft = !!entry.draftVersionId;
    let versionId = hadDraft
      ? entry.draftVersionId!
      : await createDraftVersion(entry);
    if (!versionId) throw new Error('no version id');

    if (hadDraft) {
      try {
        await axiosInstance.put(
          `admin/legal-documents/${entry.id}/versions/${versionId}`,
          { translations: buildTranslations(entry) },
          { headers: { 'x-client-app': 'admin-back-office' } },
        );
      } catch (e: any) {
        // Le brouillon en cache a été publié entre-temps (hors de cet écran) :
        // on force une nouvelle version puis on retente une seule fois.
        if (e?.response?.data?.error === 'LegalVersionNotEditableException') {
          entry.draftVersionId = null;
          versionId = await getOrCreateDraft(entry);
          if (!versionId) throw e;
          await axiosInstance.put(
            `admin/legal-documents/${entry.id}/versions/${versionId}`,
            { translations: buildTranslations(entry) },
            { headers: { 'x-client-app': 'admin-back-office' } },
          );
        } else {
          throw e;
        }
      }
    }

    // On connaît déjà le bon versionId (retourné par getOrCreateDraft/PUT) :
    // pas de refetch ici, pour éviter qu'un délai de cohérence du GET écrase
    // entry.draftVersionId avec un état serveur pas encore à jour.
    entry.draftVersionId = versionId;
    entry.saved = true;
    setTimeout(() => {
      entry.saved = false;
    }, 2500);
  } catch (e: any) {
    if (e?.response?.data?.error === 'LegalVersionNotEditableException') {
      entry.draftVersionId = null;
      entry.needsNewVersion = true;
      entry.error = t('legal.versionAlreadyPublished');
    } else {
      const msg = e?.response?.data?.message;
      entry.error = Array.isArray(msg)
        ? msg.join(' ')
        : (msg ?? t('terms.publishError'));
    }
  } finally {
    entry.saving = false;
  }
}

// ── Publish the existing draft ─────────────────────────────────────────────────
async function publishDraft(entry: DocEntry) {
  if (!entry.draftVersionId) return;
  entry.saving = true;
  entry.saved = false;
  entry.error = null;
  entry.needsNewVersion = false;
  try {
    await axiosInstance.post(
      `admin/legal-documents/${entry.id}/versions/${entry.draftVersionId}/publish`,
      entry.effectiveFrom
        ? { effectiveFrom: new Date(entry.effectiveFrom).toISOString() }
        : {},
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    await loadVersions(entry);
    entry.saved = true;
    setTimeout(() => {
      entry.saved = false;
    }, 2500);
  } catch (e: any) {
    if (e?.response?.data?.error === 'LegalVersionNotEditableException') {
      // Le brouillon en cache a déjà été publié entre-temps (hors de cet
      // écran) : on le signale et on propose de créer une nouvelle version.
      entry.draftVersionId = null;
      entry.needsNewVersion = true;
      entry.error = t('legal.versionAlreadyPublished');
    } else {
      const msg = e?.response?.data?.message;
      entry.error = Array.isArray(msg)
        ? msg.join(' ')
        : (msg ?? t('terms.publishError'));
    }
  } finally {
    entry.saving = false;
  }
}

// ── Create panel ─────────────────────────────────────────────────────────────
const creatingNew = ref(false);
const newDocType = ref<DocType | ''>('');
const newDocLevel = ref<DocLevel | ''>('');
const newDocTitle = ref('');
const newDocContent = ref('');
const newOrgSearch = ref('');
const newOrgId = ref('');
const newStoreSearch = ref('');
const newStoreId = ref('');
const orgDropdownOpen = ref(false);
const storeDropdownOpen = ref(false);
const createError = ref<string | null>(null);
const creating = ref(false);

interface OrgOption {
  id: string;
  name: string;
}
interface StoreOption {
  id: string;
  name: string;
}
const orgOptions = ref<OrgOption[]>([]);
const storeOptions = ref<StoreOption[]>([]);

async function loadOrgOptions() {
  try {
    const res = await axiosInstance.get(
      'organizations/search?pagination=true&pageSize=200&sortBy=name&sortOrder=ASC',
    );
    const list = res.data?.data ?? res.data ?? [];
    orgOptions.value = list.map((o: any) => ({
      id: o.id,
      name: o.name ?? o.designation ?? o.id,
    }));
  } catch {
    orgOptions.value = [];
  }
}

async function loadStoreOptions() {
  try {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    const list = res.data?.data ?? res.data ?? [];
    storeOptions.value = list.map((r: any) => ({
      id: r.id,
      name: r.name ?? r.designation ?? r.id,
    }));
  } catch {
    storeOptions.value = [];
  }
}

const filteredOrgOptions = computed(() =>
  newOrgSearch.value
    ? orgOptions.value.filter((o) =>
        o.name.toLowerCase().includes(newOrgSearch.value.toLowerCase()),
      )
    : orgOptions.value,
);
const filteredStoreOptions = computed(() =>
  newStoreSearch.value
    ? storeOptions.value.filter((s) =>
        s.name.toLowerCase().includes(newStoreSearch.value.toLowerCase()),
      )
    : storeOptions.value,
);

function selectOrg(opt: OrgOption) {
  newOrgId.value = opt.id;
  newOrgSearch.value = opt.name;
  orgDropdownOpen.value = false;
}
function selectStore(opt: StoreOption) {
  newStoreId.value = opt.id;
  newStoreSearch.value = opt.name;
  storeDropdownOpen.value = false;
}

function openCreateNew() {
  newDocType.value = '';
  newDocLevel.value = '';
  newDocTitle.value = '';
  newDocContent.value = '';
  newOrgId.value = '';
  newOrgSearch.value = '';
  newStoreId.value = '';
  newStoreSearch.value = '';
  createError.value = null;
  creatingNew.value = true;
  if (!orgOptions.value.length) loadOrgOptions();
  if (!storeOptions.value.length) loadStoreOptions();
}
function cancelCreateNew() {
  creatingNew.value = false;
}

const MIN_CONTENT_LENGTH = 10;
const canConfirmCreate = computed(() => {
  if (!newDocType.value || !newDocLevel.value) return false;
  if (newDocContent.value.trim().length < MIN_CONTENT_LENGTH) return false;
  if (newDocLevel.value === 'ORGANIZATION' && !newOrgId.value) return false;
  if (newDocLevel.value === 'STORE' && !newStoreId.value) return false;
  return true;
});

async function confirmCreateNew() {
  if (!canConfirmCreate.value) return;
  creating.value = true;
  createError.value = null;
  try {
    const title =
      newDocTitle.value.trim() || defaultTitle(newDocType.value as DocType);
    const content = newDocContent.value.trim();
    const body: Record<string, unknown> = {
      type: newDocType.value,
      translations: [
        { languageCode: 'fr', title, content },
        { languageCode: 'en', title, content },
        { languageCode: 'nl', title, content },
      ],
    };
    if (newDocLevel.value === 'ORGANIZATION')
      body.organizationId = newOrgId.value;
    if (newDocLevel.value === 'STORE') body.storeId = newStoreId.value;

    const res = await axiosInstance.post('admin/legal-documents', body, {
      headers: { 'x-client-app': 'admin-back-office' },
    });
    const created = res.data?.data ?? res.data;
    const entry = makeEntry({
      id: created.id,
      type: newDocType.value as DocType,
      level: newDocLevel.value as DocLevel,
      organizationId:
        newDocLevel.value === 'ORGANIZATION' ? newOrgId.value : null,
      restaurantId: newDocLevel.value === 'STORE' ? newStoreId.value : null,
    });
    if (newDocLevel.value === 'ORGANIZATION')
      entry.organizationName = newOrgSearch.value;
    if (newDocLevel.value === 'STORE')
      entry.restaurantName = newStoreSearch.value;
    await loadVersions(entry);
    docs.value.unshift(entry);
    creatingNew.value = false;
    openEditor(entry);
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    createError.value = Array.isArray(msg)
      ? msg.join(' ')
      : (msg ?? t('terms.publishError'));
  } finally {
    creating.value = false;
  }
}

function defaultTitle(type: DocType): string {
  return t(`legal.types.${type}`);
}

// ── Editor state ─────────────────────────────────────────────────────────────
const editingId = ref<string | null>(null);
const viewMode = ref<'view' | 'edit'>('view');
const editingEntry = computed(
  () => docs.value.find((d) => d.id === editingId.value) ?? null,
);

async function openEditor(entry: DocEntry, mode: 'view' | 'edit' = 'view') {
  editingId.value = entry.id;
  viewMode.value = mode;
  if (mode === 'edit') {
    try {
      await getOrCreateDraft(entry);
    } catch {
      // édition possible malgré tout, saveDraft réessaiera
    }
  }
}

async function switchToEdit() {
  if (!editingEntry.value) return;
  viewMode.value = 'edit';
  try {
    await getOrCreateDraft(editingEntry.value);
  } catch {
    // édition possible malgré tout, saveDraft réessaiera
  }
}

function closeEditor() {
  editingId.value = null;
  viewMode.value = 'view';
}

// ── Labels ───────────────────────────────────────────────────────────────────
function levelLabel(level: DocLevel) {
  if (level === 'PLATFORM') return t('terms.levelPlatform');
  if (level === 'ORGANIZATION') return t('terms.levelOrganization');
  return t('terms.levelStore');
}
function levelClass(level: DocLevel) {
  if (level === 'PLATFORM') return 'tsp__badge--platform';
  if (level === 'ORGANIZATION') return 'tsp__badge--org';
  return 'tsp__badge--store';
}
function entityLabel(entry: DocEntry) {
  if (entry.level === 'ORGANIZATION')
    return entry.organizationName || entry.organizationId;
  if (entry.level === 'STORE')
    return entry.restaurantName || entry.restaurantId;
  return '';
}

// ── Markdown preview ──────────────────────────────────────────────────────────
function renderMarkdown(md: string): string {
  if (!md) return '';
  return md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<oli>$1</oli>')
    .replace(/^---$/gm, '<hr>')
    .split(/\n{2,}/)
    .map((block) => {
      block = block.trim();
      if (!block) return '';
      if (/^<(h[1-6]|hr|ul|ol|li|oli)/.test(block)) return block;
      if (block.includes('<li>')) return '<ul>' + block + '</ul>';
      if (block.includes('<oli>'))
        return (
          '<ol>' +
          block.replace(/<\/?oli>/g, (m) =>
            m === '<oli>' ? '<li>' : '</li>',
          ) +
          '</ol>'
        );
      return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
    })
    .join('\n');
}

onMounted(loadAll);
</script>

<template>
  <div class="tsp2">
    <!-- ── List view ────────────────────────────────────────────────────────── -->
    <template v-if="!editingId">
      <div class="tsp2__header">
        <div>
          <h1 class="tsp2__title">{{ t('terms.title') }}</h1>
          <p class="tsp2__sub">{{ t('terms.pageSubtitle') }}</p>
        </div>
        <div v-if="!creatingNew" class="tsp2__header-actions">
          <div class="tsp2__filter-wrap">
            <label class="tsp2__field-label">{{ t('legal.filterType') }}</label>
            <select v-model="typeFilter" class="tsp2__field-input">
              <option value="">{{ t('legal.allTypes') }}</option>
              <option v-for="type in DOC_TYPES" :key="type" :value="type">
                {{ t(`legal.types.${type}`) }}
              </option>
            </select>
          </div>
          <div class="tsp2__filter-wrap">
            <label class="tsp2__field-label">{{
              t('terms.filterLevel')
            }}</label>
            <select v-model="levelFilter" class="tsp2__field-input">
              <option value="">{{ t('terms.allLevels') }}</option>
              <option v-for="level in DOC_LEVELS" :key="level" :value="level">
                {{ levelLabel(level) }}
              </option>
            </select>
          </div>
          <button class="tsp2__btn tsp2__btn--primary" @click="openCreateNew">
            <i class="pi pi-plus" /> {{ t('legal.createNew') }}
          </button>
        </div>
      </div>

      <!-- Create panel -->
      <div v-if="creatingNew" class="tsp2__create-panel">
        <div class="tsp2__create-row">
          <div class="tsp2__field-col">
            <label class="tsp2__field-label">{{
              t('terms.docTypeField')
            }}</label>
            <select v-model="newDocType" class="tsp2__field-input">
              <option value="" disabled>{{ t('legal.chooseType') }}</option>
              <option v-for="type in DOC_TYPES" :key="type" :value="type">
                {{ t(`legal.types.${type}`) }}
              </option>
            </select>
          </div>
          <div class="tsp2__field-col">
            <label class="tsp2__field-label">{{ t('terms.levelField') }}</label>
            <select v-model="newDocLevel" class="tsp2__field-input">
              <option value="" disabled>{{ t('terms.chooseLevel') }}</option>
              <option v-for="level in DOC_LEVELS" :key="level" :value="level">
                {{ levelLabel(level) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Organization picker -->
        <div
          v-if="newDocLevel === 'ORGANIZATION'"
          class="tsp2__field-col tsp2__search-field"
        >
          <label class="tsp2__field-label">{{
            t('terms.organizationField')
          }}</label>
          <div class="tsp2__search-wrap">
            <input
              v-model="newOrgSearch"
              class="tsp2__field-input"
              type="text"
              :placeholder="t('terms.searchOrganization')"
              @focus="orgDropdownOpen = true"
            />
            <div v-if="orgDropdownOpen" class="tsp2__dropdown">
              <button
                v-for="opt in filteredOrgOptions"
                :key="opt.id"
                type="button"
                class="tsp2__dropdown-item"
                @click="selectOrg(opt)"
              >
                {{ opt.name }}
              </button>
              <p v-if="!filteredOrgOptions.length" class="tsp2__dropdown-empty">
                {{ t('terms.noResults') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Store picker -->
        <div
          v-if="newDocLevel === 'STORE'"
          class="tsp2__field-col tsp2__search-field"
        >
          <label class="tsp2__field-label">{{ t('terms.storeField') }}</label>
          <div class="tsp2__search-wrap">
            <input
              v-model="newStoreSearch"
              class="tsp2__field-input"
              type="text"
              :placeholder="t('terms.searchStore')"
              @focus="storeDropdownOpen = true"
            />
            <div v-if="storeDropdownOpen" class="tsp2__dropdown">
              <button
                v-for="opt in filteredStoreOptions"
                :key="opt.id"
                type="button"
                class="tsp2__dropdown-item"
                @click="selectStore(opt)"
              >
                {{ opt.name }}
              </button>
              <p
                v-if="!filteredStoreOptions.length"
                class="tsp2__dropdown-empty"
              >
                {{ t('terms.noResults') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Title + content -->
        <div class="tsp2__field-col">
          <label class="tsp2__field-label">{{ t('terms.titleField') }}</label>
          <input
            v-model="newDocTitle"
            class="tsp2__field-input"
            type="text"
            :placeholder="newDocType ? t(`legal.types.${newDocType}`) : ''"
          />
        </div>
        <div class="tsp2__field-col">
          <label class="tsp2__field-label">{{ t('terms.editor') }}</label>
          <textarea
            v-model="newDocContent"
            class="tsp2__field-input tsp2__create-textarea"
            :placeholder="t('terms.editorPlaceholder')"
          />
          <span
            v-if="
              newDocContent.trim().length > 0 &&
              newDocContent.trim().length < MIN_CONTENT_LENGTH
            "
            class="tsp2__field-hint tsp2__field-hint--warn"
          >
            {{ t('terms.contentTooShort') }}
          </span>
        </div>

        <div v-if="createError" class="tsp2__error">
          <i class="pi pi-exclamation-triangle" />
          <span>{{ createError }}</span>
          <button
            class="tsp2__dismiss-btn"
            :title="t('legal.dismiss')"
            @click="createError = null"
          >
            <i class="pi pi-times" />
          </button>
        </div>

        <div class="tsp2__create-panel-actions">
          <button class="tsp2__btn tsp2__btn--ghost" @click="cancelCreateNew">
            {{ t('legal.cancel') }}
          </button>
          <button
            class="tsp2__btn tsp2__btn--primary"
            :disabled="!canConfirmCreate || creating"
            @click="confirmCreateNew"
          >
            <i v-if="creating" class="pi pi-spin pi-spinner" />
            <i v-else class="pi pi-arrow-right" />
            {{ t('legal.continue') }}
          </button>
        </div>
      </div>

      <template v-if="!creatingNew">
        <div v-if="isLoading" class="tsp2__loading">
          <i class="pi pi-spin pi-spinner" />
          {{ t('terms.loading') }}
        </div>
        <div v-else-if="loadError" class="tsp2__error">
          <i class="pi pi-exclamation-triangle" />
          {{ loadError }}
        </div>
        <div v-else-if="!filteredDocs.length" class="tsp2__empty">
          <i class="pi pi-file-o" />
          <p>{{ t('legal.noResults') }}</p>
        </div>
        <div v-else class="tsp2__list">
          <div
            v-for="entry in filteredDocs"
            :key="entry.id"
            class="tsp2__row tsp2__row--clickable"
            @click="openEditor(entry, 'view')"
          >
            <div class="tsp2__row-main">
              <div class="tsp2__row-meta">
                <span class="tsp2__badge" :class="levelClass(entry.level)">
                  {{ levelLabel(entry.level) }}
                </span>
                <span
                  v-if="entityLabel(entry)"
                  class="tsp2__badge tsp2__badge--entity"
                >
                  {{ entityLabel(entry) }}
                </span>
                <span
                  v-if="entry.publishedVersion"
                  class="tsp2__badge tsp2__badge--version"
                >
                  v{{ entry.publishedVersion }}
                </span>
                <span
                  v-if="entry.draftVersionId"
                  class="tsp2__badge tsp2__badge--draft"
                >
                  {{ t('terms.draftBadge') }}
                </span>
              </div>
              <h3 class="tsp2__row-name">
                {{ t(`legal.types.${entry.type}`) }}
              </h3>
              <p class="tsp2__row-desc">{{ t(`legal.descs.${entry.type}`) }}</p>
            </div>
            <div class="tsp2__row-actions">
              <button
                class="tsp2__btn tsp2__btn--ghost"
                @click.stop="openEditor(entry, 'view')"
              >
                <i class="pi pi-eye" /> {{ t('legal.view') }}
              </button>
              <button
                class="tsp2__icon-btn"
                :title="t('legal.edit')"
                @click.stop="openEditor(entry, 'edit')"
              >
                <i class="pi pi-pencil" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- ── Editor view ───────────────────────────────────────────────────────── -->
    <template v-else-if="editingEntry">
      <div class="tsp2__editor-header">
        <button class="tsp2__back" @click="closeEditor()">
          <i class="pi pi-arrow-left" /> {{ t('legal.backToList') }}
        </button>

        <div class="tsp2__editor-title-row">
          <div>
            <div class="tsp2__editor-eyebrow">
              <span class="tsp2__badge" :class="levelClass(editingEntry.level)">
                {{ levelLabel(editingEntry.level) }}
              </span>
              <span
                v-if="entityLabel(editingEntry)"
                class="tsp2__badge tsp2__badge--entity"
              >
                {{ entityLabel(editingEntry) }}
              </span>
              <span
                v-if="editingEntry.publishedVersion"
                class="tsp2__badge tsp2__badge--version"
              >
                v{{ editingEntry.publishedVersion }}
              </span>
            </div>
            <h2 class="tsp2__editor-name">
              {{ t(`legal.types.${editingEntry.type}`) }}
            </h2>
            <p class="tsp2__editor-sub">
              {{ t(`legal.descs.${editingEntry.type}`) }}
            </p>
          </div>

          <div v-if="viewMode === 'view'" class="tsp2__editor-actions">
            <button class="tsp2__btn tsp2__btn--primary" @click="switchToEdit">
              <i class="pi pi-pencil" /> {{ t('legal.edit') }}
            </button>
          </div>

          <div v-else class="tsp2__editor-actions">
            <button
              class="tsp2__btn tsp2__btn--ghost"
              :disabled="editingEntry.saving"
              @click="editingEntry.editorContent = ''"
            >
              {{ t('legal.reset') }}
            </button>

            <button
              class="tsp2__btn tsp2__btn--primary"
              :disabled="editingEntry.saving"
              @click="saveDraft(editingEntry)"
            >
              <i v-if="editingEntry.saving" class="pi pi-spin pi-spinner" />
              <i v-else-if="editingEntry.saved" class="pi pi-check" />
              <i v-else class="pi pi-save" />
              <span>
                {{
                  editingEntry.saving
                    ? t('terms.saving')
                    : editingEntry.draftVersionId
                      ? t('legal.updateVersion')
                      : t('legal.createDraft')
                }}
              </span>
            </button>

            <div class="tsp2__publish-wrap">
              <div class="tsp2__publish-date-field">
                <input
                  v-model="editingEntry.effectiveFrom"
                  type="datetime-local"
                  class="tsp2__field-input tsp2__publish-date"
                />
                <span class="tsp2__publish-hint">{{
                  t('legal.effectiveFromHint')
                }}</span>
              </div>
              <button
                class="tsp2__btn tsp2__btn--publish"
                :disabled="editingEntry.saving || !editingEntry.draftVersionId"
                @click="publishDraft(editingEntry)"
              >
                <i v-if="editingEntry.saving" class="pi pi-spin pi-spinner" />
                <i v-else class="pi pi-send" />
                {{
                  editingEntry.effectiveFrom
                    ? t('legal.publishScheduled')
                    : t('legal.publishVersion')
                }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="editingEntry.error" class="tsp2__error">
          <i class="pi pi-exclamation-triangle" />
          <span>{{ editingEntry.error }}</span>
          <button
            class="tsp2__dismiss-btn"
            :title="t('legal.dismiss')"
            @click="
              editingEntry.error = null;
              editingEntry.needsNewVersion = false;
            "
          >
            <i class="pi pi-times" />
          </button>
        </div>
        <div v-if="editingEntry.saved" class="tsp2__success">
          <i class="pi pi-check-circle" />
          {{ t('legal.saved') }}
        </div>
      </div>

      <!-- Title field -->
      <div v-if="viewMode === 'edit'" class="tsp2__title-field">
        <label class="tsp2__field-label">{{ t('terms.titleField') }}</label>
        <input
          v-model="editingEntry.editorTitle"
          class="tsp2__field-input"
          type="text"
          :placeholder="t(`legal.types.${editingEntry.type}`)"
        />
      </div>
      <div v-else class="tsp2__title-field">
        <label class="tsp2__field-label">{{ t('terms.titleField') }}</label>
        <p class="tsp2__title-readonly">
          {{
            editingEntry.editorTitle || t(`legal.types.${editingEntry.type}`)
          }}
        </p>
      </div>

      <!-- Split panes -->
      <div class="tsp2__split">
        <div class="tsp2__pane tsp2__pane--editor">
          <div class="tsp2__pane-head">
            <i class="pi pi-file-edit tsp2__pane-ico" />
            <span>{{ t('terms.editor') }}</span>
            <span class="tsp2__pane-badge">Markdown</span>
          </div>
          <textarea
            v-if="viewMode === 'edit'"
            v-model="editingEntry.editorContent"
            class="tsp2__textarea"
            :placeholder="t('terms.editorPlaceholder')"
          />
          <div v-else class="tsp2__textarea tsp2__textarea--readonly">
            {{ editingEntry.editorContent || t('legal.noContent') }}
          </div>
        </div>
        <div class="tsp2__divider" />
        <div class="tsp2__pane tsp2__pane--preview">
          <div class="tsp2__pane-head">
            <i class="pi pi-eye tsp2__pane-ico" />
            <span>{{ t('terms.preview') }}</span>
            <span class="tsp2__pane-badge tsp2__pane-badge--live">Live</span>
          </div>
          <div
            class="tsp2__preview"
            v-html="renderMarkdown(editingEntry.editorContent)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.tsp2 {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 2rem 2.5rem;
}

/* Header */
.tsp2__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.tsp2__title {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
}
.tsp2__sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
.tsp2__header-actions {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
}
.tsp2__filter-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 11rem;
}

/* Fields */
.tsp2__field-col {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  min-width: 12rem;
}
.tsp2__field-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}
.tsp2__field-input {
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
}
.tsp2__field-input:focus {
  border-color: var(--nxt-primary);
}

/* Create panel */
.tsp2__create-panel {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
  border-radius: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.tsp2__create-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.tsp2__create-textarea {
  height: 8rem;
  padding: 0.625rem 0.75rem;
  resize: vertical;
  font-family: 'Menlo', 'Consolas', 'SF Mono', monospace;
  line-height: 1.6;
}
.tsp2__field-hint {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}
.tsp2__field-hint--warn {
  color: #dc2626;
}
.tsp2__create-panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.tsp2__search-field {
  position: relative;
}
.tsp2__search-wrap {
  position: relative;
}
.tsp2__dropdown {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  max-height: 14rem;
  overflow-y: auto;
  z-index: 20;
  border: 1px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.tsp2__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.tsp2__dropdown-item:hover {
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.04));
}
.tsp2__dropdown-empty {
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}

/* Loading / empty */
.tsp2__loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  padding: 2rem 0;
}
.tsp2__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}
.tsp2__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

/* Badges */
.tsp2__badge {
  display: inline-flex;
  align-items: center;
  height: 1.35rem;
  padding: 0 0.5rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
}
.tsp2__badge--platform {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}
.tsp2__badge--org {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}
.tsp2__badge--store {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}
.tsp2__badge--entity {
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
  text-transform: none;
  letter-spacing: 0;
}
.tsp2__badge--version {
  background: rgba(0, 0, 0, 0.07);
  color: var(--nxt-on-surface-variant);
}
.tsp2__badge--draft {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* List */
.tsp2__list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.tsp2__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.125rem 1.25rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
}
.tsp2__row:last-child {
  border-bottom: none;
}
.tsp2__row--clickable {
  cursor: pointer;
  transition: background 0.15s ease;
}
.tsp2__row--clickable:hover {
  background: rgba(0, 0, 0, 0.02);
}
[data-theme='dark'] .tsp2__row--clickable:hover {
  background: rgba(255, 255, 255, 0.03);
}
.tsp2__row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.tsp2__row-meta {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.tsp2__row-name {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.tsp2__row-desc {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
.tsp2__row-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* Buttons */
.tsp2__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.45rem;
  border: 1.5px solid transparent;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition:
    opacity 0.15s,
    background 0.15s;
  white-space: nowrap;
}
.tsp2__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.tsp2__btn--ghost {
  background: transparent;
  color: var(--nxt-on-surface-variant);
  border-color: var(--nxt-outline-variant, #e0e0e0);
}
.tsp2__btn--ghost:hover:not(:disabled) {
  background: var(--nxt-surface-low);
}
.tsp2__btn--primary {
  background: var(--nxt-on-surface, #111);
  color: #fff;
  border-color: var(--nxt-on-surface, #111);
}
.tsp2__btn--primary:hover:not(:disabled) {
  opacity: 0.88;
}
[data-theme='dark'] .tsp2__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
.tsp2__btn--publish {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
.tsp2__btn--publish:hover:not(:disabled) {
  opacity: 0.88;
}
.tsp2__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: border-color 0.15s;
}
.tsp2__icon-btn:hover {
  border-color: #ffd60a;
  color: var(--nxt-on-surface);
}

/* Editor header */
.tsp2__editor-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
}
.tsp2__back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--nxt-primary);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  width: fit-content;
}
.tsp2__back:hover {
  text-decoration: underline;
}
.tsp2__editor-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.tsp2__editor-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.3rem;
}
.tsp2__editor-name {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
}
.tsp2__editor-sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
.tsp2__editor-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

/* Error / success */
.tsp2__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.09);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 600;
}
.tsp2__error span {
  flex: 1;
}
.tsp2__error-action {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.75rem;
  padding: 0 0.625rem;
  border: 1.5px solid currentColor;
  border-radius: 0.4rem;
  background: transparent;
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}
.tsp2__error-action:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.08);
}
.tsp2__error-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.tsp2__dismiss-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0.7;
}
.tsp2__dismiss-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}
.tsp2__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.625rem;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-size: 0.8125rem;
  font-weight: 600;
}

/* Publish */
.tsp2__publish-wrap {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.tsp2__publish-date-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.tsp2__publish-date {
  width: 12rem;
}
.tsp2__publish-hint {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}

/* Title field */
.tsp2__title-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex-shrink: 0;
}
.tsp2__title-readonly {
  margin: 0;
  padding: 0.4rem 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

/* Split editor */
.tsp2__split {
  flex: 1;
  min-height: 420px;
  display: flex;
  gap: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.tsp2__pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.tsp2__pane-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
}
.tsp2__pane-ico {
  font-size: 0.8rem;
  opacity: 0.6;
}
.tsp2__pane-badge {
  margin-left: auto;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.15rem 0.45rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant, #e0e0e0);
  color: var(--nxt-on-surface-variant);
}
.tsp2__pane-badge--live {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}
.tsp2__divider {
  width: 1px;
  background: var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  flex-shrink: 0;
}
.tsp2__textarea {
  flex: 1;
  min-height: 0;
  display: block;
  width: 100%;
  padding: 1rem 1.25rem;
  border: none;
  outline: none;
  resize: none;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: 'Menlo', 'Consolas', 'SF Mono', monospace;
  line-height: 1.8;
  box-sizing: border-box;
  overflow-y: auto;
}
.tsp2__textarea--readonly {
  white-space: pre-wrap;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.7;
}
.tsp2__textarea::placeholder {
  color: var(--nxt-on-surface-variant);
  opacity: 0.4;
}
.tsp2__preview {
  flex: 1;
  min-height: 0;
  padding: 1rem 1.5rem;
  overflow-y: auto;
  font-size: 0.9rem;
  line-height: 1.75;
  color: var(--nxt-on-surface);
}
.tsp2__preview :deep(h1) {
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0 0 0.75rem;
  color: var(--nxt-on-surface);
}
.tsp2__preview :deep(h2) {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 1.25rem 0 0.5rem;
  color: var(--nxt-on-surface);
  padding-bottom: 0.375rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
}
.tsp2__preview :deep(h3) {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 1rem 0 0.4rem;
  color: var(--nxt-on-surface);
}
.tsp2__preview :deep(p) {
  margin: 0 0 0.75rem;
  color: var(--nxt-on-surface-variant, #555);
}
.tsp2__preview :deep(strong) {
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.tsp2__preview :deep(em) {
  font-style: italic;
}
.tsp2__preview :deep(code) {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.8rem;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.05));
  padding: 0.1em 0.35em;
  border-radius: 0.25rem;
  color: var(--nxt-primary);
}
.tsp2__preview :deep(ul) {
  margin: 0 0 0.75rem 1.1rem;
  padding: 0;
  list-style: disc;
}
.tsp2__preview :deep(ol) {
  margin: 0 0 0.75rem 1.1rem;
  padding: 0;
  list-style: decimal;
}
.tsp2__preview :deep(li) {
  margin-bottom: 0.2rem;
  color: var(--nxt-on-surface-variant);
}
.tsp2__preview :deep(hr) {
  border: none;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  margin: 1.25rem 0;
}

/* Dark mode */
[data-theme='dark'] .tsp2__pane-head {
  background: rgba(255, 255, 255, 0.025);
}
[data-theme='dark'] .tsp2__textarea {
  background: transparent;
}
[data-theme='dark'] .tsp2__badge--version {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .tsp2__pane-badge {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .tsp2__field-input {
  background: transparent;
}
[data-theme='dark'] .tsp2__list,
[data-theme='dark'] .tsp2__split,
[data-theme='dark'] .tsp2__create-panel {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .tsp2__dropdown {
  background: rgba(20, 28, 41, 0.95);
  border-color: rgba(255, 255, 255, 0.1);
}
</style>
